import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet } from '../utils/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const VehiculeQRAction = () => {
  const { t } = useTranslation();
  const { tenantSlug, vehiculeId } = useParams();
  const navigate = useNavigate();
  const [vehicule, setVehicule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // Debug log
  console.log('🎯 VehiculeQRAction monté - tenantSlug:', tenantSlug, 'vehiculeId:', vehiculeId);

  useEffect(() => {
    console.log('🔄 VehiculeQRAction useEffect - chargement véhicule');
    checkAuthentication();
    loadVehicule();
  }, [vehiculeId, tenantSlug]);

  const checkAuthentication = () => {
    // Vérifier si un token existe
    const token = localStorage.getItem(`${tenantSlug}_token`);
    setIsAuthenticated(!!token);
  };

  const loadVehicule = async () => {
    try {
      setLoading(true);
      // Utiliser l'endpoint public (sans authentification)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/${tenantSlug}/actifs/vehicules/${vehiculeId}/public`
      );
      
      if (!response.ok) {
        throw new Error(t('vehiculeQr.vehiculeNonTrouve'));
      }
      
      const data = await response.json();
      setVehicule(data);
    } catch (err) {
      console.error('Erreur chargement véhicule:', err);
      setError(t('vehiculeQr.vehiculeNonTrouve'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/${tenantSlug}/auth/login`,
        {
          email: email,
          mot_de_passe: password
        }
      );

      // Sauvegarder le token
      localStorage.setItem(`${tenantSlug}_token`, response.data.access_token);
      localStorage.setItem(`${tenantSlug}_user`, JSON.stringify(response.data.user));
      
      // Stocker le tenant si présent dans la réponse
      if (response.data.tenant) {
        localStorage.setItem(`${tenantSlug}_tenant`, JSON.stringify(response.data.tenant));
      }
      
      // L'action qr_action est déjà sauvegardée par handleRondeSecurite ou handleInventaire
      // avant l'affichage du formulaire de connexion - on ne touche pas!
      const existingAction = localStorage.getItem('qr_action');
      console.log('✅ Login réussi, qr_action en attente:', existingAction);
      
      // IMPORTANT: Forcer la page courante vers 'actifs' pour que l'AppLayout
      // affiche directement la bonne page après redirection
      localStorage.setItem('currentPage', 'actifs');
      console.log('📍 currentPage forcé à "actifs" pour redirection QR');
      
      setIsAuthenticated(true);
      
      // Rediriger vers l'application principale
      // AppLayout détectera qr_action et ouvrira le bon modal
      console.log('🔄 Redirection vers:', `/${tenantSlug}`);
      window.location.href = `/${tenantSlug}`;
    } catch (err) {
      console.error('Erreur connexion:', err);
      setLoginError(t('vehiculeQr.emailOuMotDePasseIncorrect'));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleRondeSecurite = () => {
    // Sauvegarder les infos de l'action AVANT de vérifier l'authentification
    // Ainsi, après connexion, l'action sera disponible
    const qrActionData = {
      action: 'ronde_securite',
      vehicule_id: vehiculeId,
      vehicule: vehicule,
      tenant_slug: tenantSlug,
      timestamp: Date.now()
    };
    localStorage.setItem('qr_action', JSON.stringify(qrActionData));
    console.log('💾 QR Action sauvegardée (ronde):', qrActionData);
    
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }
    
    // Forcer la page courante vers 'actifs' pour redirection
    localStorage.setItem('currentPage', 'actifs');
    console.log('📍 currentPage forcé à "actifs" pour ronde de sécurité');
    
    // Rediriger vers la page principale de l'application
    window.location.href = `/${tenantSlug}`;
  };

  // Requêtes du véhicule (voir / signaler un problème) : même chemin que la ronde et
  // l'inventaire — l'action est gardée, la connexion demandée au besoin, puis Gestion
  // des actifs ouvre la liste des requêtes de CE véhicule.
  const handleRequetes = () => {
    localStorage.setItem('qr_action', JSON.stringify({
      action: 'requetes',
      vehicule_id: vehiculeId,
      vehicule: vehicule,
      tenant_slug: tenantSlug,
      timestamp: Date.now()
    }));
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }
    localStorage.setItem('currentPage', 'actifs');
    window.location.href = `/${tenantSlug}`;
  };

  const handleInventaire = () => {
    // Sauvegarder les infos de l'action AVANT de vérifier l'authentification
    const qrActionData = {
      action: 'inventaire',
      vehicule_id: vehiculeId,
      vehicule: vehicule,
      tenant_slug: tenantSlug,
      timestamp: Date.now()
    };
    localStorage.setItem('qr_action', JSON.stringify(qrActionData));
    console.log('💾 QR Action sauvegardée (inventaire):', qrActionData);
    
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }
    
    // Forcer la page courante vers 'actifs' pour redirection
    localStorage.setItem('currentPage', 'actifs');
    console.log('📍 currentPage forcé à "actifs" pour inventaire');
    
    // Rediriger vers la page principale de l'application
    window.location.href = `/${tenantSlug}`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #DC2626',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#6B7280' }}>{t('vehiculeQr.chargement')}</p>
        </div>
      </div>
    );
  }

  if (error || !vehicule) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h2 style={{ color: '#DC2626', marginBottom: '10px' }}>{t('vehiculeQr.erreur')}</h2>
          <p style={{ color: '#6B7280' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '16px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        width: '100%'
      }}>
        {/* En-tête avec icône véhicule */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#FEE2E2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '40px'
          }}>
            🚒
          </div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '10px'
          }}>
            {vehicule.nom}
          </h1>
          <p style={{
            color: '#6B7280',
            fontSize: '1rem',
            marginBottom: '5px'
          }}>
            {vehicule.type_vehicule} • {vehicule.marque || 'N/A'}
          </p>
          {vehicule.numero_plaque && (
            <p style={{
              color: '#9CA3AF',
              fontSize: '0.875rem'
            }}>
              {t('vehiculeQr.plaqueNumeroPlaque', { numero_plaque: vehicule.numero_plaque })}
            </p>
          )}
        </div>

        {/* Statut de connexion */}
        {!isAuthenticated && !showLogin && (
          <div style={{
            backgroundColor: '#FEF3C7',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center',
            border: '1px solid #FCD34D'
          }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400E' }}>
              {t('vehiculeQr.iConnexionRequisePourEffectuerUne')}
            </p>
          </div>
        )}

        {/* Titre section */}
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#374151',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          {showLogin ? t('vehiculeQr.connexion2') : t('vehiculeQr.queSouhaitezVousFaire')}
        </h2>

        {/* Formulaire de connexion */}
        {showLogin ? (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {loginError && (
              <div style={{
                backgroundColor: '#FEE2E2',
                color: '#DC2626',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                {loginError}
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                {t('vehiculeQr.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                placeholder={t('vehiculeQr.votreEmailExampleCom')}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                {t('vehiculeQr.motDePasse')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loggingIn}
              style={{
                backgroundColor: '#DC2626',
                color: 'white',
                padding: '14px',
                borderRadius: '8px',
                border: 'none',
                cursor: loggingIn ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                opacity: loggingIn ? 0.6 : 1
              }}
            >
              {loggingIn ? t('vehiculeQr.connexion') : t('vehiculeQr.seConnecter')}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowLogin(false);
                setLoginError('');
                setEmail('');
                setPassword('');
              }}
              style={{
                backgroundColor: 'transparent',
                color: '#6B7280',
                padding: '10px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              {t('vehiculeQr.retour')}
            </button>
          </form>
        ) : (
          /* Boutons d'action */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {/* Bouton Ronde de Sécurité */}
            <button
            onClick={handleRondeSecurite}
            style={{
              backgroundColor: '#DC2626',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.125rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              transition: 'all 0.2s',
              boxShadow: '0 4px 6px rgba(220, 38, 38, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#B91C1C';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(220, 38, 38, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#DC2626';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.2)';
            }}
          >
            <span style={{ fontSize: '24px' }}>🔧</span>
            <span>{t('vehiculeQr.rondeDeSecuriteSaaq')}</span>
          </button>

          {/* Bouton Inventaire — grisé si aucun formulaire d'inventaire actif
              n'est configuré pour ce véhicule. Évite que l'utilisateur clique
              et tombe sur une modal vide. */}
          <button
            onClick={vehicule.has_active_inventaire_form === false ? undefined : handleInventaire}
            disabled={vehicule.has_active_inventaire_form === false}
            title={
              vehicule.has_active_inventaire_form === false
                ? t('vehiculeQr.aucunFormulaireDInventaireActifN')
                : undefined
            }
            style={{
              backgroundColor: vehicule.has_active_inventaire_form === false ? '#D1D5DB' : '#8B5CF6',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: 'none',
              cursor: vehicule.has_active_inventaire_form === false ? 'not-allowed' : 'pointer',
              fontSize: '1.125rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              transition: 'all 0.2s',
              boxShadow: vehicule.has_active_inventaire_form === false
                ? 'none'
                : '0 4px 6px rgba(139, 92, 246, 0.2)',
              opacity: vehicule.has_active_inventaire_form === false ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (vehicule.has_active_inventaire_form === false) return;
              e.currentTarget.style.backgroundColor = '#7C3AED';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(139, 92, 246, 0.3)';
            }}
            onMouseLeave={(e) => {
              if (vehicule.has_active_inventaire_form === false) return;
              e.currentTarget.style.backgroundColor = '#8B5CF6';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(139, 92, 246, 0.2)';
            }}
          >
            <span style={{ fontSize: '24px' }}>📦</span>
            <span>
              {vehicule.has_active_inventaire_form === false
                ? t('vehiculeQr.inventaireIndisponible')
                : t('vehiculeQr.demarrerUnInventaire')}
            </span>
          </button>
          {vehicule.has_active_inventaire_form === false && (
            <p style={{
              fontSize: '0.75rem',
              color: '#9CA3AF',
              textAlign: 'center',
              margin: '-5px 0 0 0',
              fontStyle: 'italic',
            }}>
              {t('vehiculeQr.aucunFormulaireDInventaireConfigurePour')}
            </p>
          )}

          {/* Requêtes : voir celles du véhicule ou signaler un problème (photo du cellulaire) */}
          <button
            onClick={handleRequetes}
            style={{
              backgroundColor: '#E67E22',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.125rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              boxShadow: '0 4px 6px rgba(230, 126, 34, 0.2)'
            }}
          >
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <span>{t('vehiculeQr.requetes')}</span>
          </button>
          </div>
        )}

        {/* Footer */}
        <div style={{
          marginTop: '30px',
          paddingTop: '20px',
          borderTop: '1px solid #E5E7EB',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: '#9CA3AF',
            margin: 0
          }}>
            {t('vehiculeQr.profiremanagerGestionDesActifs')}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VehiculeQRAction;
